/**
 * Admin dashboard page component.
 */
import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import { listUsers } from '../api/users';
import { UserListResponse } from '../api/users';
import { getOpenStackStatistics, OpenStackStatistics } from '../api/openstack';
import './AdminDashboard.css';

// Helper function to format GB values, showing TB if >= 1000 GB
const formatStorage = (gb: number): { value: number; unit: string } => {
  if (gb >= 1000) {
    return { value: gb / 1000, unit: 'TB' };
  }
  return { value: gb, unit: 'GB' };
};

export const AdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const { isConnected, on, off } = useWebSocket();
  const [users, setUsers] = useState<UserListResponse[]>([]);
  const [openstackStats, setOpenstackStats] = useState<OpenStackStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [userList, stats] = await Promise.all([
        listUsers(),
        getOpenStackStatistics().catch(() => null), // Don't fail if stats fail
      ]);
      setUsers(userList);
      setOpenstackStats(stats);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load data';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.is_admin) {
      loadData();
    }
  }, [user]);

  // Register WebSocket event handlers for user and settings updates
  useEffect(() => {
    if (!user?.is_admin) return;

    const handleUserUpdate = async (message: { event: string; data?: any }) => {
      const { event_type, user_id, user_data } = message.data || {};
      
      if (event_type === 'added') {
        // New user was added - reload stats
        await loadData();
      } else if (event_type === 'updated') {
        // User was updated - update in list if data is provided
        if (user_data) {
          setUsers(prevUsers => {
            const index = prevUsers.findIndex(u => u.id === user_id);
            if (index >= 0) {
              const updated = [...prevUsers];
              updated[index] = user_data as UserListResponse;
              return updated;
            }
            return prevUsers;
          });
        } else {
          // Reload if no data provided
          await loadData();
        }
      } else if (event_type === 'deleted') {
        // User was deleted - reload stats
        await loadData();
      }
    };

    const handleSettingsUpdate = async (message: { event: string; data?: any }) => {
      // Settings updated - could affect stats, but stats are computed from database
      // No need to reload unless we want to show settings changes
    };

    const unsubscribeUser = on('user_update', handleUserUpdate);
    const unsubscribeSettings = on('settings_update', handleSettingsUpdate);

    return () => {
      unsubscribeUser();
      unsubscribeSettings();
    };
  }, [user, on, off]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeUsers = users.filter(u => u.is_active).length;
  const inactiveUsers = users.filter(u => !u.is_active).length;
  const adminUsers = users.filter(u => u.is_admin).length;

  // Format RAM and Disk stats
  const ramAllocated = openstackStats ? formatStorage(openstackStats.total_ram_gb_allocated) : null;
  const ramQuota = openstackStats?.total_ram_gb_quota !== null && openstackStats?.total_ram_gb_quota !== undefined
    ? formatStorage(openstackStats.total_ram_gb_quota)
    : null;
  const diskAllocated = openstackStats ? formatStorage(openstackStats.total_disk_gb_allocated) : null;
  const diskQuota = openstackStats?.total_disk_gb_quota !== null && openstackStats?.total_disk_gb_quota !== undefined
    ? formatStorage(openstackStats.total_disk_gb_quota)
    : null;

  return (
    <AdminLayout>
      <div className="admin-dashboard-page">
        <div className="admin-dashboard-container">
          <h1>Admin Dashboard</h1>
          <p className="page-description">Welcome, {user?.name || user?.email}</p>

          {error && (
            <div className="error-message" role="alert">
              <strong>Error:</strong> {error}
            </div>
          )}

          {loading ? (
            <div className="loading-message">Loading statistics...</div>
          ) : (
            <>
              <h2 className="stats-section-title">User Statistics</h2>
              <div className="admin-stats admin-stats-users">
                <div className="stat-card">
                  <h3>Total Users</h3>
                  <p className="stat-number">{users.length}</p>
                </div>
                <div className="stat-card">
                  <h3>Active Users</h3>
                  <p className="stat-number">{activeUsers}</p>
                </div>
                <div className="stat-card">
                  <h3>Inactive Users</h3>
                  <p className="stat-number">{inactiveUsers}</p>
                </div>
                <div className="stat-card">
                  <h3>Administrators</h3>
                  <p className="stat-number">{adminUsers}</p>
                </div>
              </div>

              {openstackStats && (
                <>
                  <h2 className="stats-section-title">OpenStack Statistics</h2>
                  <div className="admin-stats">
                    <div className="stat-card">
                      <h3>Total Clouds</h3>
                      <p className="stat-number">{openstackStats.total_clouds}</p>
                    </div>
                    <div className="stat-card">
                      <h3>Total Projects</h3>
                      <p className="stat-number">{openstackStats.total_projects}</p>
                    </div>
                    <div className="stat-card">
                      <h3>Total OpenStack Users</h3>
                      <p className="stat-number">{openstackStats.total_openstack_users}</p>
                    </div>
                  </div>
                  <div className="admin-stats admin-stats-quotas">
                    <div className="stat-card">
                      <h3>Total VMs</h3>
                      <p className="stat-number">
                        {openstackStats.total_vms_allocated}
                        {openstackStats.total_vms_quota !== null && (
                          <span className="stat-quota"> / {openstackStats.total_vms_quota}</span>
                        )}
                      </p>
                    </div>
                    <div className="stat-card">
                      <h3>Total Cores</h3>
                      <p className="stat-number">
                        {openstackStats.total_cores_allocated}
                        {openstackStats.total_cores_quota !== null && (
                          <span className="stat-quota"> / {openstackStats.total_cores_quota}</span>
                        )}
                      </p>
                    </div>
                    <div className="stat-card">
                      <h3>Total RAM ({ramAllocated?.unit || 'GB'})</h3>
                      <p className="stat-number">
                        {ramAllocated && (
                          <>
                            {ramAllocated.value.toFixed(2)}
                            {ramQuota && (
                              <span className="stat-quota"> / {ramQuota.value.toFixed(2)}</span>
                            )}
                          </>
                        )}
                      </p>
                    </div>
                    <div className="stat-card">
                      <h3>Total Disk ({diskAllocated?.unit || 'GB'})</h3>
                      <p className="stat-number">
                        {diskAllocated && (
                          <>
                            {diskAllocated.value.toFixed(2)}
                            {diskQuota && (
                              <span className="stat-quota"> / {diskQuota.value.toFixed(2)}</span>
                            )}
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

